'use client';
import fallbackImage from '@shared/public/images/ring/thumbnail_placeholder.png';
import clsx from 'clsx';
import Image from 'next/image';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { Button } from '@headlessui/react';
import BDImage from '@shared/components/elements/bd-image';
import { TagByMetalType, getProductFilterLabel } from '@shared/lib/search/product';
import {
  JewelryProduct,
  JewelryVariant,
  RingImage,
  ShortJewelryProduct,
  ShortJewelryVariant,
} from '@shared/lib/shopify/types/shopify-native';
import { getMoneyRange } from '@shared/lib/utils/money';
import { filterRingMedia, groupRingMedia } from '@shared/lib/utils/product-utils';
import Link from 'next/link';
import { Metal } from '../metal';

interface MetalSelectorProps {
  className?: string;
  availableMetals: string[];
  selectedMetal: string | undefined;
  setSelectedMetal: Dispatch<SetStateAction<string | undefined>>;
}

const MetalSelector: React.FC<MetalSelectorProps> = ({
  className,
  availableMetals,
  selectedMetal,
  setSelectedMetal,
}) => (
  <ul role="list" className={clsx(className, 'flex items-center justify-center gap-4')}>
    {availableMetals.map((metal) => (
      <li key={metal}>
        <Button
          className="group peer"
          onClick={() => setSelectedMetal(selectedMetal === metal ? undefined : metal)}
        >
          <div className="relative flex min-w-0 flex-col items-center">
            <Metal
              metal={metal}
              className={clsx(
                selectedMetal === metal ? 'border-bd-primary border-2' : 'border-transparent',
                'group-hover:border-bd-primary h-6 w-6 group-hover:border-2'
              )}
            />
            <p
              className={clsx(
                selectedMetal === metal ? 'block' : 'hidden',
                'text-xxs text-bd-dark/80 absolute whitespace-nowrap py-6 capitalize group-hover:z-10 group-hover:block'
              )}
            >
              {getProductFilterLabel('metal', metal)}
            </p>
          </div>
        </Button>
      </li>
    ))}
  </ul>
);

export default function JewelryListItem({
  product,
  metal,
  cutFilters,
}: {
  product: ShortJewelryProduct | JewelryProduct;
  metal?: string;
  cutFilters?: string[];
}) {
  const [selectedMetal, setSelectedMetal] = useState<string | undefined>(metal);
  useEffect(() => {
    setSelectedMetal(metal);
  }, [metal, setSelectedMetal]);
  const params = new URLSearchParams();
  if (selectedMetal) {
    params.set('metal', selectedMetal);
  }
  if (cutFilters && cutFilters.length > 0) {
    params.set('cut', cutFilters[0]!);
  }

  const metalType = TagByMetalType[selectedMetal as keyof typeof TagByMetalType];
  const selectedColor =
    metalType == '14K yellow gold' ? 'yellow' : metalType == '14K rose gold' ? 'rose' : 'white';

  const { images: ringImages, availableImageColors } = groupRingMedia(product.images, []);

  const availableMetals = [
    ...availableImageColors.map((color) => `${color}_gold`),
    availableImageColors.includes('white') && 'platinum',
  ].filter(Boolean) as string[];

  const { result: imagesToDisplay } = filterRingMedia(ringImages, selectedColor, cutFilters) as {
    result: RingImage[];
    exactMatch: boolean;
  };
  const primaryImage = imagesToDisplay[0];
  const secondaryImage = imagesToDisplay[1 % imagesToDisplay.length];

  const selectedVariants = (product.variants as (ShortJewelryVariant | JewelryVariant)[]).filter(
    (variant: ShortJewelryVariant | JewelryVariant) => {
      return !metalType || variant.metal === metalType;
    }
  );
  const priceRange = getMoneyRange(
    selectedVariants.map((variant: ShortJewelryVariant | JewelryVariant) => variant.price)
  );
  return (
    <div key={product.handle}>
      <Link
        href={`jewelry/${product.handle}${params.toString() ? `?${params.toString()}` : ''}`}
        className="cursor-pointer"
      >
        <div className="group relative aspect-square w-full overflow-hidden rounded-lg bg-white">
          {primaryImage && secondaryImage ? (
            <>
              <BDImage
                src={primaryImage}
                className="object-cover opacity-100 group-hover:opacity-0 lg:object-contain"
              />
              <BDImage
                src={secondaryImage}
                className="absolute inset-0 object-contain opacity-0 group-hover:opacity-100"
              />
            </>
          ) : (
            <Image
              className="h-1/2 w-1/2 object-cover"
              src={fallbackImage}
              alt={'Image not available'}
            />
          )}
        </div>
      </Link>
      <div className="flex flex-col items-center gap-2 py-2 text-center">
        <MetalSelector
          availableMetals={availableMetals}
          selectedMetal={selectedMetal}
          setSelectedMetal={setSelectedMetal}
        />
        <h4 className="text-sm font-normal">
          {/* <span className="absolute inset-0" /> */}
          {product.title}
        </h4>
        <p className="text-bd-dark/80 -mt-2 text-sm">
          {priceRange.min === priceRange.max
            ? `$${priceRange.min.toLocaleString()}`
            : `$${priceRange.min.toLocaleString()} - $${priceRange.max.toLocaleString()}`}
        </p>
      </div>
    </div>
  );
}
